import { useState, useCallback, useMemo, useRef, useEffect } from "react";
import { Lock, Loader2, X } from "lucide-react";
import { toast } from "sonner";
import {
  Drawer,
  DrawerContent,
  DrawerTitle,
  DrawerDescription,
} from "@/components/ui/drawer";
import { Button } from "@/components/ui/button";
import PromptBar from "@/components/chat/PromptBar";
import { useInfiniteRecentPosts } from "@/hooks/useRecentStashes";
import { useSearchStashes, type SearchStashesPage } from "@/hooks/useSearchStashes";
import { useDebounce } from "@/hooks/useDebounce";
import { useSearchLoadingMessage } from "@/hooks/useSearchLoadingMessage";
import { useAddToCollection, useCollectionRecommendations } from "@/hooks/useCollections";
import type { CollectionItem, CollectionItemType } from "@/types/collections";
import type { StashThumbnailData } from "@/components/shared/StashThumbnail/StashThumbnail";
import { StashThumbnailGrid } from "@/components/shared/StashThumbnail/StashThumbnailGrid";
import { StashThumbnailMasonry } from "@/components/shared/StashThumbnail/StashThumbnailMasonry";
import { useIsMobile } from "@/hooks/use-mobile";
import { mapRecommendedToThumbnail } from "@/components/collections/CollectionRecommendations";
import { EntityConfirmationStep } from "@/components/collections/EntityConfirmationStep";
import { PrivacyWarningDialog } from "@/components/collections/PrivacyWarningDialog";
import { useBatchStashEntities } from "@/hooks/useBatchStashEntities";
import { useEntityConfirmationFlow } from "@/hooks/useEntityConfirmationFlow";
import { buildAddSuccessLabel } from "@/utils/entityMappers";
import { HEADLINE, LABEL, META } from "@/lib/typography";
import { cn } from "@/lib/utils";

interface AddItemsSheetProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  collectionId: string;
  collectionTitle?: string;
  existingItems: CollectionItem[];
  collectionVisibility?: "private" | "public";
  /** Called with all entity IDs that were shown during confirmation, regardless of selection */
  onEntitiesOffered?: (entityIds: string[]) => void;
}

export function AddItemsSheet({
  open,
  onOpenChange,
  collectionId,
  collectionTitle,
  existingItems,
  collectionVisibility,
  onEntitiesOffered,
}: AddItemsSheetProps) {
  const isMobile = useIsMobile();
  const [search, setSearch] = useState("");
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [privateWarningOpen, setPrivateWarningOpen] = useState(false);
  const debouncedSearch = useDebounce(search.trim(), 300);
  const isSearching = debouncedSearch.length > 0;

  const recentQuery = useInfiniteRecentPosts(30);
  const searchQuery = useSearchStashes(debouncedSearch, 30);

  const activeQuery = isSearching
    ? searchQuery
    : recentQuery;
  const { fetchNextPage, hasNextPage, isFetchingNextPage, isFetching } = activeQuery;
  const searchLoadingMessage = useSearchLoadingMessage(isSearching && isFetching);

  const addMutation = useAddToCollection();

  // Recommendations
  const { data: recsData } = useCollectionRecommendations(open ? collectionId : undefined);
  const recThumbnails = useMemo(() => {
    if (!recsData?.items?.length) return [];
    return recsData.items.map(mapRecommendedToThumbnail);
  }, [recsData]);

  const existingStashIds = useMemo(
    () => new Set(existingItems.filter((i) => i.item_type === "stash").map((i) => i.item_id)),
    [existingItems],
  );

  const displayPosts = useMemo(() => {
    const searchPages = searchQuery.data?.pages as SearchStashesPage[] | undefined;
    const all = (isSearching
      ? (searchPages?.flatMap(page => page.posts) ?? [])
      : recentQuery.posts) as StashThumbnailData[];
    const filtered = all.filter((p) => !existingStashIds.has(p.id));

    if (!isSearching) {
      const postIds = new Set(filtered.map((p) => p.id));
      const uniqueRecs = recThumbnails.filter(
        (p) => !existingStashIds.has(p.id) && !postIds.has(p.id),
      );
      return [...uniqueRecs, ...filtered];
    }

    return filtered;
  }, [existingStashIds, isSearching, recentQuery.posts, recThumbnails, searchQuery.data]);

  const handleSearchSubmit = useCallback(() => {
    // PromptBar blurs on touch devices to dismiss keyboard
  }, []);

  const handleSearchClear = useCallback(() => {
    setSearch("");
  }, []);

  // Fetch entities only when user enters the confirm step (not on every toggle)
  const [entityFetchEnabled, setEntityFetchEnabled] = useState(false);
  const selectedIds = useMemo(() => Array.from(selected), [selected]);
  const { entities: relatedEntities, hasEntities: hasRelatedEntities, isFetching: entitiesFetching } =
    useBatchStashEntities(selectedIds, open && entityFetchEnabled);

  const fireAdd = useCallback(
    (includeEntities: boolean, entityIds: Set<string>) => {
      if (selected.size === 0) return;
      const stashItems: Array<{ type: CollectionItemType; id: string }> =
        Array.from(selected).map((id) => ({ type: "stash" as const, id }));
      const entityItems: Array<{ type: CollectionItemType; id: string }> =
        includeEntities
          ? Array.from(entityIds).map((id) => ({ type: "entity" as const, id }))
          : [];
      const combinedItems = [...stashItems, ...entityItems];

      addMutation.mutate(
        { collectionId, items: combinedItems },
        {
          onSuccess: () => {
            // Mark all shown entities as offered so the "new discoveries" banner
            // doesn't re-prompt for entities the user just declined
            if (relatedEntities.length > 0) {
              onEntitiesOffered?.(relatedEntities.map((e) => e.entity_id));
            }
            toast.success(buildAddSuccessLabel({
              stashCount: selected.size,
              entityCount: entityItems.length,
            }));
            onOpenChange(false);
          },
          onError: () => {
            toast.error("Failed to add items. Please try again.");
          },
        },
      );
    },
    [selected, collectionId, addMutation, onOpenChange, relatedEntities, onEntitiesOffered],
  );

  // Two-step entity confirmation flow
  const confirmationFlow = useEntityConfirmationFlow({
    entities: relatedEntities,
    hasEntities: hasRelatedEntities,
    isFetching: entitiesFetching,
    onAdd: (includeEntities, entityIds) => fireAdd(includeEntities, entityIds),
  });

  // Reset all local state when the drawer transitions from open → closed.
  // Covers user-initiated close (drag / X) and programmatic close (after add).
  const resetRef = useRef(confirmationFlow.reset);
  resetRef.current = confirmationFlow.reset;
  const wasOpen = useRef(open);
  useEffect(() => {
    if (wasOpen.current && !open) {
      setSearch("");
      setSelected(new Set());
      setEntityFetchEnabled(false);
      resetRef.current();
    }
    wasOpen.current = open;
  }, [open]);

  const toggleSelect = useCallback((postId: string) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(postId)) next.delete(postId);
      else next.add(postId);
      return next;
    });
  }, []);

  const allDisplaySelected = displayPosts.length > 0 && displayPosts.every((p) => selected.has(p.id));

  const toggleSelectAll = useCallback(() => {
    setSelected((prev) => {
      if (allDisplaySelected) {
        const next = new Set(prev);
        for (const p of displayPosts) next.delete(p.id);
        return next;
      }
      const next = new Set(prev);
      for (const p of displayPosts) next.add(p.id);
      return next;
    });
  }, [displayPosts, allDisplaySelected]);

  // Infinite scroll — scrollRoot uses useState so that when Vaul's portal
  // actually mounts (which can lag behind the `open` prop by a render cycle),
  // the state change triggers a re-render and the effect picks up the DOM node.
  const loadMoreRef = useRef<HTMLDivElement>(null);
  const [scrollRoot, setScrollRoot] = useState<HTMLDivElement | null>(null);
  useEffect(() => {
    const el = loadMoreRef.current;
    if (!el || !scrollRoot || !hasNextPage || isFetchingNextPage) return;
    const observer = new IntersectionObserver(
      (entries) => { if (entries[0]?.isIntersecting) fetchNextPage(); },
      { root: scrollRoot, rootMargin: "200px" },
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [scrollRoot, hasNextPage, isFetchingNextPage, fetchNextPage]);

  const privateSelectedCount = useMemo(() => {
    if (collectionVisibility !== "public" || selected.size === 0) return 0;
    return displayPosts.filter(
      (p) => selected.has(p.id) && p.visibility === "private",
    ).length;
  }, [collectionVisibility, selected, displayPosts]);

  const handleAdd = useCallback(() => {
    if (selected.size === 0) return;
    if (privateSelectedCount > 0) {
      setPrivateWarningOpen(true);
      return;
    }
    setEntityFetchEnabled(true);
    confirmationFlow.startConfirmation();
  }, [selected, privateSelectedCount, confirmationFlow]);

  const handleGoBack = useCallback(() => {
    setEntityFetchEnabled(false);
    confirmationFlow.goBack();
  }, [confirmationFlow]);

  // Overlay for private stash lock icon
  const getItemOverlay = useCallback(
    (post: StashThumbnailData) => {
      if (post.visibility !== "private") return undefined;
      return (
        <div className="absolute top-1.5 left-1.5 z-10 flex h-5 w-5 items-center justify-center rounded-full bg-black/50">
          <Lock className="h-3 w-3 text-white" />
        </div>
      );
    },
    [],
  );

  return (
    <>
      <Drawer open={open} onOpenChange={onOpenChange}>
        <DrawerContent className="h-[85vh]" disableInnerScroll>
          <DrawerTitle className={cn(HEADLINE, "px-4 pt-4 pb-2")}>
            Add stashes{collectionTitle ? ` to ${collectionTitle}` : ""}
          </DrawerTitle>
          <DrawerDescription className="sr-only">
            Search and select stashes to add
          </DrawerDescription>

          {confirmationFlow.step === "confirm" ? (
            <EntityConfirmationStep
              entities={relatedEntities}
              selectedEntityIds={confirmationFlow.selectedEntityIds}
              onToggleEntity={confirmationFlow.toggleEntity}
              stashCount={selected.size}
              collectionTitle={collectionTitle ?? "collection"}
              onToggleAll={confirmationFlow.toggleAll}
              onToggleGroup={confirmationFlow.toggleGroup}
              onAdd={confirmationFlow.confirmAdd}
              onBack={handleGoBack}
              isPending={addMutation.isPending}
              isLoading={entitiesFetching}
            />
          ) : (
            <div className="flex flex-col h-full min-h-0">
              {/* Search — fixed above scroll */}
              <div className="shrink-0 px-4 pb-3" data-no-keyboard-reposition>
                <div className="flex items-center gap-2">
                  <div className="flex-1">
                    <PromptBar
                      value={search}
                      onChange={setSearch}
                      onSubmit={handleSearchSubmit}
                      onClear={handleSearchClear}
                      placeholder="Search stashes..."
                      submitIcon={search.trim() ? X : undefined}
                    />
                  </div>
                  <button
                    type="button"
                    onClick={toggleSelectAll}
                    disabled={displayPosts.length === 0}
                    className={cn(LABEL, "shrink-0 text-primary hover:text-primary/80 transition-colors whitespace-nowrap disabled:opacity-40 disabled:pointer-events-none")}
                  >
                    {allDisplaySelected ? "Deselect all" : "Select all"}
                  </button>
                </div>
              </div>

              {/* Scrollable content */}
              <div ref={setScrollRoot} className="flex-1 overflow-y-auto min-h-0 px-4 pb-4">

                {/* Loading state — only show spinner if nothing to display yet */}
                {isFetching && displayPosts.length === 0 && (
                  <div className="flex flex-col items-center justify-center py-8 gap-3 text-center">
                    <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
                    {isSearching && (
                      <p className={META} style={{ animation: "fadeIn 300ms ease-in" }}>
                        {searchLoadingMessage}
                      </p>
                    )}
                  </div>
                )}
                {!isFetching && displayPosts.length === 0 && (
                  <p className={cn(META, "text-center py-8")}>
                    {isSearching ? `No stashes found for "${debouncedSearch}"` : "No stashes found"}
                  </p>
                )}

                {/* Stash grid (mobile) / masonry (desktop) */}
                {displayPosts.length > 0 && (
                  isMobile ? (
                    <StashThumbnailGrid
                      posts={displayPosts}
                      columns={{ base: 2, sm: 3 }}
                      isEditMode
                      selectedIds={selected}
                      onToggleSelect={toggleSelect}
                      getItemOverlay={getItemOverlay}
                    />
                  ) : (
                    <StashThumbnailMasonry
                      posts={displayPosts}
                      isEditMode
                      selectedIds={selected}
                      onToggleSelect={toggleSelect}
                      getItemOverlay={getItemOverlay}
                    />
                  )
                )}

                {isFetchingNextPage && (
                  <div className="flex justify-center py-4">
                    <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
                  </div>
                )}
                {hasNextPage && <div ref={loadMoreRef} className="h-1 w-full" />}
                {!hasNextPage && !isFetchingNextPage && displayPosts.length > 0 && (
                  <p className={cn(META, "text-center py-4")}>You're all caught up.</p>
                )}
              </div>

              {/* Footer — slides up when items are selected */}
              <div
                className="shrink-0 z-30 border-t bg-background px-4 py-3 flex justify-center transition-all duration-200 ease-out"
                style={{
                  maxHeight: selected.size > 0 ? "60px" : "0px",
                  opacity: selected.size > 0 ? 1 : 0,
                  padding: selected.size > 0 ? undefined : "0 1rem",
                  borderTopWidth: selected.size > 0 ? undefined : "0",
                  overflow: "hidden",
                }}
              >
                <Button
                  variant="gradient"
                  size="sm"
                  onClick={handleAdd}
                  disabled={selected.size === 0 || addMutation.isPending}
                  className="cursor-pointer"
                >
                  {addMutation.isPending ? "Adding..." : `Add ${selected.size} ${selected.size === 1 ? "item" : "items"}`}
                </Button>
              </div>
            </div>
          )}
        </DrawerContent>
      </Drawer>

      <PrivacyWarningDialog
        open={privateWarningOpen}
        onOpenChange={setPrivateWarningOpen}
        title="Share private stashes?"
        description={
          privateSelectedCount === 1
            ? "1 of your selected stashes is private. Anyone can follow this public collection and see it."
            : `${privateSelectedCount} of your selected stashes are private. Anyone can follow this public collection and see them.`
        }
        onConfirm={() => {
          setPrivateWarningOpen(false);
          setEntityFetchEnabled(true);
          confirmationFlow.startConfirmation();
        }}
      />
    </>
  );
}
